/**
 * /podcast-editor — связка: экран, жесты, прослушка, выдача.
 *
 * Модули рядом отвечают каждый за своё и ничего не знают про DOM:
 *   model.js        — выпуск как документ: дорожки, клипы, главы, история;
 *   dsp.js          — измерения: громкость по BS.1770, речь, паузы;
 *   engine.js       — сведение: план усилений и отсчёты (прослушка = файл);
 *   sync.js         — сдвиг и расхождение часов записей с разных устройств;
 *   chapters-id3.js — главы внутри MP3;
 *   store.js        — разбор файла в запись редактора;
 *   player.js       — прослушка;
 *   export.js       — сборка выпуска в файл;
 *   timeline.js     — шкала.
 * Здесь — только склейка и состояние экрана.
 */

import {
    makeDoc, addTrack, removeTrack, trackOf, clipOf, clipsOf, clipLen, clipEnd, duration, splitAt,
    rippleDelete, liftRange, cropTo, shortenPauses, moveClip, shiftTrack, trimClip, deleteClip, setSpeed,
    addChapter, removeChapter, normalizeChapters, chapterList, chaptersAsText, chaptersAsJson,
    makeHistory, toProject, fromProject, bindSources, restore, snapshot,
    ROLE_VOICE, ROLE_MUSIC, ROLE_RAW, LIMITS, MAX_TRACKS,
} from './model.js';
import { buildPlan, findPauses } from './engine.js';
import { syncOffset } from './sync.js';
import { importFile } from './store.js';
import { createPlayer } from './player.js';
import { exportEpisode, isLossless } from './export.js';
import { createTimeline, fmtClock, RULER_H } from './timeline.js';

const CFG = window.__PE || { slug: 'podcast-editor', lang: {} };
const L = CFG.lang || {};
const SLUG = CFG.slug || 'podcast-editor';

const $ = (id) => document.getElementById(id);
const ae = (name, data) => { try { window.__ae && window.__ae(name, Object.assign({ slug: SLUG }, data || {})); } catch (_) {} };
const toast = (m, kind) => { try { if (m && window.__toast) window.__toast(m, kind || 'info'); } catch (_) {} };
const say = (m) => { try { if (m && window.__announce) window.__announce(m); } catch (_) {} };
const setText = (el, t) => { if (!el) return; if (window.__setText) return window.__setText(el, t); el.textContent = t; };
const fill = (tpl, map) => String(tpl || '').replace(/:([a-z_]+)/g, (m, k) => (map && map[k] != null ? map[k] : m));
const esc = (s) => { const d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; };
const tickPage = () => (window.__yieldTick ? window.__yieldTick(0) : new Promise((r) => setTimeout(r, 0)));

/* ── состояние ───────────────────────────────────────────────────────── */
const doc = makeDoc();
const history = makeHistory(doc);
const sources = new Map();
let pending = [];                   // записи открытого проекта, которых ещё нет: {id, fileName}
let plan = null;
let timeline = null;
let player = null;
let selTrack = null;
let selClip = null;
let range = null;
let cover = null;                   // {data:Uint8Array, mime, w, h, name, url}
let exportAbort = null;
let result = null;                  // {blob, name, receipt, chapters}
let busy = 0;
let draft = null;
let restartTimer = 0;
let raf = 0;
let pauseList = null;
let gestureSnap = null;
const lastErrors = [];              // для стендов: почему запись не разобралась

const channelsWanted = () => {
    const v = $('pe-m-channels') ? $('pe-m-channels').value : 'auto';
    return v === '1' ? 1 : v === '2' ? 2 : 'auto';
};
const dbText = (v) => (v > 0 ? '+' : v < 0 ? '−' : '') + Math.abs(v).toFixed(1) + ' ' + (L.unitDb || 'dB');
const lufsText = (v) => (isFinite(v) ? (v < 0 ? '−' : '') + Math.abs(v).toFixed(1) : '—');

/* ── пересчёт и перерисовка ──────────────────────────────────────────── */
function replan(opts) {
    plan = buildPlan(doc, sources, { channels: channelsWanted() });
    if (timeline) {
        const n = doc.tracks.length;
        timeline.setLaneHeight(n > 7 ? 44 : n > 4 ? 52 : 60);
        timeline.resize();
        timeline.redraw();
    }
    paintNumbers();
    paintHeads();
    if (player && player.isPlaying() && !(opts && opts.keepAudio)) scheduleRestart();
    if (draft && !(opts && opts.quiet)) draft.touch(!(opts && opts.minor));
}

/** Картинка отвечает на ручку сразу, звук — с придержкой: полсотни пересборок в секунду слышны как треск. */
function scheduleRestart() {
    clearTimeout(restartTimer);
    restartTimer = setTimeout(() => { if (player.isPlaying()) player.refresh(); }, 140);
}

function paintNumbers() {
    const d = plan ? plan.duration : 0;
    setText($('pe-total'), fmtClock(d));
    setText($('pe-exp-dur'), fmtClock(d));
    $('pe-undo').disabled = !history.canUndo();
    $('pe-redo').disabled = !history.canRedo();
    const chip = $('pe-loud');
    if (plan && isFinite(plan.estLufs)) {
        const from = lufsText(plan.estLufs);
        chip.hidden = false;
        chip.textContent = plan.targetLufs != null
            ? fill(L.loudTo, { from, to: lufsText(plan.targetLufs) })
            : fill(L.loudAsIs, { from });
        chip.title = plan.targetLufs != null
            ? fill(L.loudToTitle, { gain: dbText(plan.masterDb) })
            : (L.loudAsIsTitle || '');
    } else {
        chip.hidden = true;
    }
    const hasRange = !!(range && Math.abs(range.a - range.b) > 0.02);
    $('pe-cut').disabled = !hasRange;
    $('pe-crop').disabled = !hasRange;
    $('pe-silence').disabled = !hasRange || !selTrack;
    $('pe-split').disabled = !doc.clips.length;
    $('pe-chapter').disabled = !doc.clips.length;
    $('pe-export').disabled = !!exportAbort || !doc.clips.length || busy > 0;
    const voices = doc.tracks.filter((t) => t.role === ROLE_VOICE).length;
    $('pe-sync').disabled = voices < 2 || busy > 0;
    $('pe-pauses').disabled = !voices;
}

/* ── шапки дорожек ───────────────────────────────────────────────────── */
const ROLE_ICON = {
    [ROLE_VOICE]: '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0014 0M12 18v3"/></svg>',
    [ROLE_MUSIC]: '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l11-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="17" cy="16" r="3"/></svg>',
    [ROLE_RAW]: '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M3 12h3l2-6 4 12 3-9 2 3h4"/></svg>',
};

function paintHeads() {
    const box = $('pe-heads');
    const want = doc.tracks.map((t) => t.id).join(',');
    if (box.dataset.ids !== want) {
        box.dataset.ids = want;
        box.innerHTML = doc.tracks.map((t) => (
            '<div class="pe-head" data-id="' + t.id + '">'
            + '<button type="button" class="pe-head-pick" data-act="pick" data-tap-skip aria-pressed="false">'
            + '<i class="pe-head-ico"></i><span class="pe-head-name" dir="auto"></span></button>'
            + '<div class="pe-head-row">'
            + '<button type="button" class="pe-head-b" data-act="mute" data-tap-skip aria-pressed="false" title="' + esc(L.mute) + '" aria-label="' + esc(L.mute) + '">M</button>'
            + '<button type="button" class="pe-head-b" data-act="solo" data-tap-skip aria-pressed="false" title="' + esc(L.solo) + '" aria-label="' + esc(L.solo) + '">S</button>'
            + '<span class="pe-head-gain"></span>'
            + '</div></div>'
        )).join('');
    }
    doc.tracks.forEach((t, i) => {
        const el = box.children[i];
        if (!el) return;
        el.style.setProperty('--pe-track', t.color);
        el.classList.toggle('on', t.id === selTrack);
        const ico = el.querySelector('.pe-head-ico');
        if (ico.dataset.role !== t.role) { ico.dataset.role = t.role; ico.innerHTML = ROLE_ICON[t.role] || ''; }
        setText(el.querySelector('.pe-head-name'), t.name || (L.track + ' ' + (i + 1)));
        const pick = el.querySelector('[data-act=pick]');
        pick.setAttribute('aria-pressed', t.id === selTrack ? 'true' : 'false');
        pick.title = (L['role_' + t.role] || '') + ' · ' + (t.name || '');
        const m = el.querySelector('[data-act=mute]');
        const s = el.querySelector('[data-act=solo]');
        m.classList.toggle('on', !!t.mute); m.setAttribute('aria-pressed', t.mute ? 'true' : 'false');
        s.classList.toggle('on', !!t.solo); s.setAttribute('aria-pressed', t.solo ? 'true' : 'false');
        const pt = plan ? plan.tracks[i] : null;
        const total = (t.gainDb || 0) + (pt ? pt.auto : 0);
        setText(el.querySelector('.pe-head-gain'), dbText(total));
    });
    box.style.setProperty('--pe-lane-h', (timeline ? timeline.state.laneH : 60) + 'px');
}

/* ── выбор и разбор дорожки ──────────────────────────────────────────── */
function select(trackId, clipId, opts) {
    selTrack = trackId && trackOf(doc, trackId) ? trackId : null;
    selClip = clipId && clipOf(doc, clipId) ? clipId : null;
    if (timeline) timeline.setSelected(selTrack, selClip);
    fillTrackTab();
    paintHeads();
    paintNumbers();
    if (opts && opts.show && selTrack) showTab('track');
}

function fillTrackTab() {
    const t = selTrack ? trackOf(doc, selTrack) : null;
    $('pe-track-empty').hidden = !!t;
    $('pe-track-form').hidden = !t;
    if (!t) return;
    const i = doc.tracks.indexOf(t);
    if (document.activeElement !== $('pe-t-name')) $('pe-t-name').value = t.name || '';
    document.querySelectorAll('#pe-t-role .tc-seg-b').forEach((b) => {
        const on = b.dataset.role === t.role;
        b.classList.toggle('on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    $('pe-t-gain').value = String(t.gainDb || 0);
    setText($('pe-t-gain-v'), dbText(t.gainDb || 0));
    $('pe-t-mute').setAttribute('aria-pressed', t.mute ? 'true' : 'false');
    $('pe-t-solo').setAttribute('aria-pressed', t.solo ? 'true' : 'false');
    const pt = plan ? plan.tracks[i] : null;
    const auto = pt ? pt.auto : 0;
    const note = $('pe-t-auto');
    if (doc.mix.level && t.role !== ROLE_RAW && Math.abs(auto) >= 0.05) {
        note.hidden = false;
        note.textContent = fill(t.role === ROLE_MUSIC ? L.autoMusic : L.autoVoice, { gain: dbText(auto) });
    } else {
        note.hidden = true;
    }
    if (document.activeElement !== $('pe-t-speed')) $('pe-t-speed').value = String(Math.round((t.speed || 1) * 100000) / 100000);
    const list = clipsOf(doc, t.id);
    const first = list.length ? Math.min.apply(null, list.map((c) => c.at)) : 0;
    const startField = $('pe-t-start');
    startField.dataset.value = String(first);
    if (startField.__timeField) startField.__timeField.refresh(); else if (document.activeElement !== startField) startField.value = fmtClock(first, true);

    const c = selClip ? clipOf(doc, selClip) : null;
    $('pe-clip-form').hidden = !c;
    if (c) {
        if (document.activeElement !== $('pe-c-fi')) $('pe-c-fi').value = String(Math.round((c.fadeIn || 0) * 10) / 10);
        if (document.activeElement !== $('pe-c-fo')) $('pe-c-fo').value = String(Math.round((c.fadeOut || 0) * 10) / 10);
        setText($('pe-c-len'), fmtClock(clipLen(doc, c), true));
    }
}

/** Правка выбранной дорожки с шагом истории. `live` — ручку ещё тянут: шаг один на весь жест. */
let liveKey = null;
function editTrack(fn, key, live) {
    const t = selTrack ? trackOf(doc, selTrack) : null;
    if (!t) return;
    if (!live || liveKey !== key) history.push();
    liveKey = live ? key : null;
    fn(t);
    replan({ minor: !!live });
    fillTrackTab();
}

/* ── вкладки ─────────────────────────────────────────────────────────── */
function showTab(name, instant) {
    const apply = () => {
        document.querySelectorAll('#pe-tabs .tc-seg-b').forEach((b) => {
            const on = b.dataset.tab === name;
            b.classList.toggle('on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
            b.tabIndex = on ? 0 : -1;
        });
        ['track', 'sound', 'episode'].forEach((k) => { $('pe-tab-' + k).hidden = k !== name; });
    };
    // Переход — украшение: когда сразу за сменой вкладки идёт действие с её
    // содержимым (фокус в новую главу), вкладка открывается без него.
    if (!instant && window.__viewTransition && $('pe-tab-' + name).hidden) window.__viewTransition(apply);
    else apply();
    if (name !== 'sound' && pauseList) closePauses();
}

/* ── добавление записей ──────────────────────────────────────────────── */
function setBusy(text, frac) {
    const el = $('pe-busy');
    if (text == null) { el.hidden = true; return; }
    el.hidden = false;
    setText($('pe-busy-txt'), text);
    $('pe-busy-fill').style.width = frac != null ? Math.round(frac * 100) + '%' : '';
    $('pe-busy-fill').classList.toggle('run', frac == null);
}

async function addFiles(files, source) {
    const list = Array.from(files || []);
    if (!list.length) return;
    const room = MAX_TRACKS - doc.tracks.length;
    // Записи открытого проекта садятся на свои места и новых дорожек не занимают.
    const mine = (f) => pending.some((p) => sameName(p.fileName, f.name));
    let take = list;
    const fresh = list.filter((f) => !mine(f));
    if (fresh.length > room) {
        toast(fill(L.maxTracks, { n: MAX_TRACKS }), 'warning');
        const keep = new Set(fresh.slice(0, Math.max(0, room)));
        take = list.filter((f) => mine(f) || keep.has(f));
    }
    if (!take.length) return;

    stopPlayback();
    busy += 1;
    paintNumbers();
    const ctx = player.decodeContext();
    let added = 0;
    const wasEmpty = !doc.tracks.length;
    try {
        for (let i = 0; i < take.length; i++) {
            const file = take[i];
            setBusy(fill(L.decoding, { name: file.name, i: i + 1, n: take.length }), null);
            let src;
            try {
                src = await importFile(file, ctx, {
                    slug: SLUG,
                    role: source === 'mic_record' ? ROLE_VOICE : null,
                    limitKey: window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null,
                });
            } catch (err) {
                lastErrors.push(String((err && (err.code || err.name)) || '') + ': ' + String((err && err.message) || err).slice(0, 160));
                ae('tool_error', { error_type: 'decode_failed', code: (err && (err.code || err.name)) || '', ext: (file.name.split('.').pop() || '').toLowerCase().slice(0, 6) });
                // Отказ по памяти — не «формат не тот»: о нём говорит общая фраза ядра (она называет причину
                // и что делать), а своя «не удалось прочитать» отправила бы человека перекодировать запись зря.
                const mem = err && (err.name === 'MemoryBudgetError' || err.code === 'MEMORY_BUDGET' || /allocation failed|out of memory|not able to be created/i.test(String(err.message || '')));
                try { window.__handleError && window.__handleError(err, 'pe_decode', mem ? undefined : { say: fill(L.decodeFail, { name: file.name }) }); } catch (_) { toast(fill(L.decodeFail, { name: file.name }), 'error'); }
                continue;
            }
            if (!(src.duration > 0.05)) { toast(fill(L.tooShort, { name: file.name }), 'warning'); continue; }
            sources.set(src.id, src);
            const row = pending.find((p) => sameName(p.fileName, file.name));
            if (row) {
                pending = pending.filter((p) => p !== row);
                const dur = {}; dur[src.id] = src.duration;
                const map = {}; map[row.id] = src.id;
                bindSources(doc, map, dur);
            } else {
                if (added === 0 && !wasEmpty) history.push();
                const at = source === 'mic_record' ? timeline.state.playhead : 0;
                const made = addTrack(doc, src, { role: src.role, name: file.name.replace(/\.[^.]+$/, ''), at });
                select(made.track.id, made.clip.id);
            }
            added += 1;
            reveal();
            replan();
            ae('feature_use', { f: 'track_added', role: src.role, dur: Math.round(src.duration), ch: src.ch, src: source || 'pick' });
        }
    } finally {
        busy -= 1;
        setBusy(null);
        paintNumbers();
    }
    if (added) {
        if (wasEmpty) { history.reset(); timeline.zoomFit(); try { window.__stage && window.__stage('file_loaded', { tracks: doc.tracks.length }); } catch (_) {} }
        if (pending.length) notePending();
        else if (!selTrack && doc.tracks.length) select(doc.tracks[0].id, null);
        say(fill(L.tracksNow, { n: doc.tracks.length }));
    }
}

const sameName = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

function reveal() {
    const root = $('pe');
    const has = doc.tracks.length > 0;
    if (root.classList.contains('has-tracks') === has) return;
    root.classList.toggle('has-tracks', has);
    document.documentElement.classList.toggle('pe-has-tracks', has);
    requestAnimationFrame(() => { if (timeline) timeline.resize(); });
}

function notePending() {
    const el = $('pe-pending');
    if (!pending.length) { el.hidden = true; return; }
    el.hidden = false;
    el.textContent = fill(L.pendingFiles, { list: pending.map((p) => p.fileName).join(', ') });
}

/* ── прослушка ───────────────────────────────────────────────────────── */
function paintPlay() {
    const on = player.isPlaying();
    // Атрибут, а не класс: общий .hidden приезжает поздней таблицей стилей, и до неё видны оба значка.
    $('pe-ic-play').toggleAttribute('hidden', on);
    $('pe-ic-pause').toggleAttribute('hidden', !on);
    $('pe-play').setAttribute('aria-label', on ? L.pause : L.play);
    try { if (navigator.mediaSession) navigator.mediaSession.playbackState = on ? 'playing' : 'paused'; } catch (_) {}
}

function tick() {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(function loop() {
        if (!player.isPlaying()) return;
        const t = player.time();
        timeline.setPlayhead(t, true);
        setText($('pe-now'), fmtClock(t, true));
        raf = requestAnimationFrame(loop);
    });
}

async function startPlayback(from) {
    if (!plan || !(plan.frames > 0)) return;
    let at = from != null ? from : timeline.state.playhead;
    if (at >= plan.duration - 0.05) at = 0;
    const ok = await player.play(at);
    paintPlay();
    if (ok) tick();
}

function stopPlayback() {
    if (!player || !player.isPlaying()) return;
    const at = player.stop();
    cancelAnimationFrame(raf);
    timeline.setPlayhead(at, false);
    setText($('pe-now'), fmtClock(at, true));
    paintPlay();
}

function seek(t) {
    const at = Math.max(0, Math.min(plan ? plan.duration : 0, t));
    timeline.setPlayhead(at, false);
    timeline.reveal(at);
    setText($('pe-now'), fmtClock(at, true));
    player.seek(at);
}

/** Момент, к которому относится «сейчас»: при игре — тот, что слышно. */
const nowTime = () => (player.isPlaying() ? player.heard() : timeline.state.playhead);

/* ── правки ──────────────────────────────────────────────────────────── */
function commit(fn, opts) {
    history.push();
    const out = fn();
    if (selClip && !clipOf(doc, selClip)) selClip = null;
    if (selTrack && !trackOf(doc, selTrack)) selTrack = null;
    replan();
    select(selTrack, selClip);
    if (opts && opts.clearRange) setRange(null);
    return out;
}

function setRange(r) {
    range = r && Math.abs(r.a - r.b) > 0.02 ? { a: Math.min(r.a, r.b), b: Math.max(r.a, r.b) } : null;
    if (timeline) timeline.setRange(range);
    paintNumbers();
    const el = $('pe-range-note');
    if (range) {
        el.hidden = false;
        el.textContent = fill(L.rangeNote, { from: fmtClock(range.a, true), to: fmtClock(range.b, true), len: fmtClock(range.b - range.a, true) });
    } else {
        el.hidden = true;
    }
}

function doSplit() {
    const t = nowTime();
    const ids = selTrack ? [selTrack] : null;
    history.push();
    const n = splitAt(doc, t, ids);
    if (!n) { history.dropIfUnchanged(); toast(L.splitNothing, 'info'); return; }
    replan();
    select(selTrack, selClip);
    ae('feature_use', { f: 'split', n });
}

function doCut() {
    if (!range) return;
    const r = range;
    let removed = 0;
    commit(() => { removed = rippleDelete(doc, r.a, r.b); }, { clearRange: true });
    seek(Math.min(r.a, plan.duration));
    toast(fill(L.cutDone, { len: fmtClock(removed, true) }), 'success');
    ae('feature_use', { f: 'ripple_cut', sec: Math.round(removed * 10) / 10 });
}

function doSilence() {
    if (!range || !selTrack) return;
    const r = range;
    history.push();
    if (!liftRange(doc, selTrack, r.a, r.b)) { history.dropIfUnchanged(); toast(L.silenceNothing, 'info'); return; }
    if (selClip && !clipOf(doc, selClip)) selClip = null;
    replan();
    select(selTrack, selClip);
    setRange(null);
    ae('feature_use', { f: 'silence_range' });
}

function doCrop() {
    if (!range) return;
    const r = range;
    commit(() => cropTo(doc, r.a, r.b), { clearRange: true });
    seek(0);
    ae('feature_use', { f: 'crop' });
}

function doDelete() {
    if (range) { doCut(); return; }
    if (selClip) {
        const id = selClip;
        commit(() => deleteClip(doc, id));
        select(selTrack, null);
        return;
    }
    if (selTrack) deleteTrack(selTrack);
}

function deleteTrack(id) {
    stopPlayback();
    commit(() => removeTrack(doc, id));
    select(doc.tracks.length ? doc.tracks[0].id : null, null);
    reveal();
    ae('feature_use', { f: 'track_removed' });
}

function undo() { if (history.undo()) afterHistory(); }
function redo() { if (history.redo()) afterHistory(); }
function afterHistory() {
    if (selClip && !clipOf(doc, selClip)) selClip = null;
    if (selTrack && !trackOf(doc, selTrack)) selTrack = doc.tracks.length ? doc.tracks[0].id : null;
    setRange(null);
    closePauses();
    reveal();
    replan();
    select(selTrack, selClip);
    fillSound();
    fillEpisode();
}

/* ── вкладка «Звук» ──────────────────────────────────────────────────── */
function fillSound() {
    $('pe-m-level').checked = !!doc.mix.level;
    $('pe-m-duck').checked = !!doc.mix.duck;
    $('pe-m-hpf').checked = !!doc.mix.hpf;
    $('pe-m-bleed').checked = !!doc.mix.bleed;
    $('pe-m-duckdb').value = String(doc.mix.duckDb);
    setText($('pe-m-duckdb-v'), '−' + doc.mix.duckDb + ' ' + (L.unitDb || 'dB'));
    $('pe-duck-row').hidden = !doc.mix.duck;
    $('pe-m-target').value = String(doc.mix.target);
}

async function doSync() {
    const voices = doc.tracks.filter((t) => t.role === ROLE_VOICE && clipsOf(doc, t.id).length);
    if (voices.length < 2) return;
    // Точка отсчёта — первая дорожка: сообщение тогда называет ту, что сдвинулась, и на сколько.
    const ref = voices[0];
    const firstClip = (t) => clipsOf(doc, t.id).sort((a, b) => a.at - b.at)[0];
    const zeroOf = (t) => { const c = firstClip(t); return c.at - c.in / (t.speed || 1); };
    const refSrc = sources.get(firstClip(ref).src);
    if (!refSrc) return;
    stopPlayback();
    busy += 1; paintNumbers();
    setBusy(L.syncing, null);
    await tickPage();
    const lines = [];
    let moved = 0;
    history.push();
    try {
        for (const t of voices) {
            if (t === ref) continue;
            const c = firstClip(t);
            const src = sources.get(c.src);
            if (!src) continue;
            await tickPage();
            const r = syncOffset(refSrc, src);
            if (!r.ok) {
                lines.push(fill(L.syncNone, { name: t.name, ref: ref.name }));
                ae('feature_use', { f: 'sync', ok: 0, reason: r.reason || '', r: Math.round((r.r || 0) * 100) / 100 });
                continue;
            }
            const single = clipsOf(doc, t.id).length === 1;
            let speedNote = '';
            if (single && r.speed !== 1) {
                setSpeed(doc, t.id, (ref.speed || 1) * r.speed);
                speedNote = ' ' + fill(L.syncDrift, { ms: Math.round(Math.abs(r.driftPpm) * 3.6) });
            }
            const want = zeroOf(ref) + r.lagSec / (ref.speed || 1);
            const delta = want - zeroOf(t);
            const done = shiftTrack(doc, t.id, delta);
            // Дорожка не может начаться раньше нуля: если её начало уехало бы
            // влево, вправо сдвигаются ВСЕ остальные — взаимное положение то же.
            if (done > delta + 1e-6) {
                for (const o of doc.tracks) if (o.id !== t.id) shiftTrack(doc, o.id, done - delta);
                for (const ch of doc.chapters) ch.t += done - delta;
            }
            moved += 1;
            const sign = delta >= 0 ? '+' : '−';
            lines.push(fill(L.syncDone, { name: t.name, shift: sign + Math.abs(delta).toFixed(r.exact ? 3 : 2) }) + speedNote);
            ae('feature_use', { f: 'sync', ok: 1, exact: r.exact ? 1 : 0, drift: Math.round(r.driftPpm), sec: Math.round(Math.abs(delta) * 100) / 100 });
        }
    } finally {
        busy -= 1; setBusy(null);
    }
    if (!moved) history.dropIfUnchanged();
    replan();
    select(selTrack, selClip);
    const note = $('pe-sound-note');
    note.hidden = false;
    note.className = 'tc-msg ' + (moved ? 'good' : 'warn');
    note.textContent = lines.join(' ') + (moved ? '' : ' ' + (L.syncHint || ''));
    say(note.textContent);
}

function openPauses() {
    $('pe-pause-panel').hidden = false;
    $('pe-pauses').setAttribute('aria-expanded', 'true');
    scanPauses();
}

function closePauses() {
    if (!$('pe-pause-panel')) return;
    $('pe-pause-panel').hidden = true;
    $('pe-pauses').setAttribute('aria-expanded', 'false');
    pauseList = null;
    if (timeline) timeline.setPauses(null);
}

function scanPauses() {
    const min = parseFloat($('pe-p-min').value) || 1.5;
    pauseList = plan ? findPauses(plan, min).map((p) => ({ from: p.from, to: p.to, off: false })) : [];
    paintPauses();
}

function paintPauses() {
    const keep = parseFloat($('pe-p-keep').value) || 0.7;
    const on = pauseList.filter((p) => !p.off && p.to - p.from > keep + 0.05);
    const saved = on.reduce((s, p) => s + (p.to - p.from - keep), 0);
    $('pe-p-sum').textContent = pauseList.length
        ? fill(L.pauseSum, { n: on.length, total: pauseList.length, saved: fmtClock(saved, true) })
        : (L.pauseNone || '');
    $('pe-p-apply').disabled = !on.length;
    const box = $('pe-p-list');
    box.innerHTML = pauseList.slice(0, 300).map((p, i) => (
        '<label class="pe-p-row"><input type="checkbox" data-i="' + i + '"' + (p.off ? '' : ' checked') + '>'
        + '<button type="button" class="pe-p-go" data-tap-skip data-go="' + i + '">' + fmtClock(p.from) + '</button>'
        + '<span>' + (p.to - p.from).toFixed(1) + ' ' + esc(L.unitSec) + '</span></label>'
    )).join('');
    timeline.setPauses(pauseList);
}

function applyPauses() {
    const keep = parseFloat($('pe-p-keep').value) || 0.7;
    const on = pauseList.filter((p) => !p.off);
    let saved = 0;
    commit(() => { saved = shortenPauses(doc, on, keep); });
    closePauses();
    toast(fill(L.pauseDone, { n: on.length, saved: fmtClock(saved, true) }), 'success');
    ae('feature_use', { f: 'pauses_shortened', n: on.length, sec: Math.round(saved) });
}

/* ── вкладка «Выпуск» ────────────────────────────────────────────────── */
function fillEpisode() {
    const m = doc.meta;
    [['pe-e-title', 'title'], ['pe-e-show', 'show'], ['pe-e-author', 'author'], ['pe-e-num', 'episode'], ['pe-e-year', 'year']]
        .forEach(([id, k]) => { if (document.activeElement !== $(id)) $(id).value = m[k] || ''; });
    paintChapters();
    paintCover();
}

const untitled = (n) => fill(L.chapterN, { n });

function paintChapters() {
    const box = $('pe-ch-list');
    const rows = doc.chapters.slice().sort((a, b) => a.t - b.t);
    $('pe-ch-empty').hidden = rows.length > 0;
    $('pe-ch-tools').hidden = rows.length === 0;
    const want = rows.map((c) => c.id).join(',');
    if (box.dataset.ids !== want) {
        box.dataset.ids = want;
        box.innerHTML = rows.map((c) => (
            '<div class="pe-ch" data-id="' + c.id + '">'
            + '<button type="button" class="pe-ch-t" data-tap-skip data-act="go"></button>'
            + '<input class="tc-in pe-ch-name" type="text" maxlength="120" dir="auto" aria-label="' + esc(L.chapterName) + '">'
            + '<button type="button" class="pe-ico" data-act="del" title="' + esc(L.chapterDel) + '" aria-label="' + esc(L.chapterDel) + '">'
            + '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button>'
            + '</div>'
        )).join('');
    }
    rows.forEach((c, i) => {
        const el = box.children[i];
        if (!el) return;
        setText(el.querySelector('.pe-ch-t'), fmtClock(c.t));
        const inp = el.querySelector('.pe-ch-name');
        inp.placeholder = untitled(i + 1);
        if (document.activeElement !== inp) inp.value = c.title || '';
    });
    if (timeline) timeline.redraw();
}

function addChapterNow() {
    const t = nowTime();
    let ch = null;
    const before = doc.chapters.length;
    history.push();
    ch = addChapter(doc, t, '');
    if (doc.chapters.length === before) { history.dropIfUnchanged(); }
    replan({ minor: true });
    showTab('episode', true);
    paintChapters();
    const row = document.querySelector('.pe-ch[data-id="' + ch.id + '"] .pe-ch-name');
    if (row) { try { row.focus({ preventScroll: true }); } catch (_) { row.focus(); } }
    ae('feature_use', { f: 'chapter_added', n: doc.chapters.length });
}

function paintCover() {
    const has = !!cover;
    $('pe-e-cover-img').hidden = !has;
    $('pe-e-cover-del').hidden = !has;
    if (has) $('pe-e-cover-img').src = cover.url;
    const note = $('pe-e-cover-note');
    if (!has) { note.textContent = L.coverHint || ''; note.className = 'tc-note'; return; }
    const good = cover.w === cover.h && cover.w >= 1400;
    note.className = 'tc-note ' + (good ? 'pe-good' : 'pe-warn');
    note.textContent = fill(good ? L.coverGood : L.coverOdd, { w: cover.srcW, h: cover.srcH });
}

async function pickCover(file) {
    if (!file) return;
    try {
        const bmp = await createImageBitmap(file);
        const side = Math.min(1400, Math.max(bmp.width, bmp.height));
        const k = side / Math.max(bmp.width, bmp.height);
        const w = Math.max(1, Math.round(bmp.width * k));
        const h = Math.max(1, Math.round(bmp.height * k));
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const g = cv.getContext('2d');
        g.fillStyle = '#fff';                       // JPEG без альфы: прозрачное становится белым, а не чёрным
        g.fillRect(0, 0, w, h);
        g.drawImage(bmp, 0, 0, w, h);
        const srcW = bmp.width;
        const srcH = bmp.height;
        try { bmp.close(); } catch (_) {}
        const blob = window.__canvasBlob
            ? await window.__canvasBlob(cv, 'image/jpeg', 0.9)
            : await new Promise((r) => cv.toBlob(r, 'image/jpeg', 0.9));
        const data = new Uint8Array(await blob.arrayBuffer());
        if (cover && cover.url) { try { URL.revokeObjectURL(cover.url); } catch (_) {} }
        cover = { data, mime: 'image/jpeg', w, h, srcW, srcH, name: file.name, blob, url: URL.createObjectURL(blob) };
        doc.meta.cover = { name: file.name, w: srcW, h: srcH };
        paintCover();
        if (draft) draft.touch(true);
        ae('feature_use', { f: 'cover_set', w: srcW, h: srcH });
    } catch (err) {
        try { window.__handleError && window.__handleError(err, 'pe_cover', { say: L.coverFail }); } catch (_) { toast(L.coverFail, 'error'); }
    }
}

/* ── выдача ──────────────────────────────────────────────────────────── */
function fillFormats() {
    const sel = $('pe-format');
    sel.innerHTML = [['mp3', 'MP3'], ['m4a', 'M4A (AAC)'], ['wav', 'WAV'], ['flac', 'FLAC']]
        .map(([v, t]) => '<option value="' + v + '">' + t + '</option>').join('');
    sel.value = 'mp3';
    if (window.__toolSettings) window.__toolSettings.bind(SLUG, sel, 'format', 'mp3');
    const q = $('pe-quality');
    q.innerHTML = [64, 96, 128, 160, 192, 256, 320].map((k) => '<option value="' + k + '">' + k + ' kbps</option>').join('');
    q.value = '128';
    if (window.__toolSettings) window.__toolSettings.bind(SLUG, q, 'kbps', '128');
    if (window.QualityTier && window.QualityTier.rescan) { try { window.QualityTier.rescan(); } catch (_) {} }
    const sync = () => { $('pe-quality-f').hidden = isLossless(sel.value); };
    sel.addEventListener('change', sync);
    sync();
}

function showProgress(on, txt, pct) {
    $('pe-prog').hidden = !on;
    if (txt != null) setText($('pe-prog-txt'), txt);
    if (pct != null) {
        $('pe-prog-fill').style.width = Math.round(pct * 100) + '%';
        setText($('pe-prog-pct'), Math.round(pct * 100) + '%');
    }
    $('pe-cancel').hidden = !on;
    $('pe-export').hidden = !!on;
}

function limitKey() {
    const used = new Set(doc.clips.map((c) => c.src));
    const keys = [];
    sources.forEach((s) => { if (used.has(s.id) && s.limitKey) keys.push(s.limitKey); });
    return keys.length ? 'set:' + keys.sort().join('|') : null;
}

function baseName() {
    const title = (doc.meta.title || '').trim();
    if (title) return title;
    const voice = doc.tracks.find((t) => t.role === ROLE_VOICE) || doc.tracks[0];
    return ((voice && voice.name) || L.outputBase || 'podcast') + (L.outputSuffix || '');
}

async function doExport() {
    if (exportAbort || !doc.clips.length) return;
    if (busy > 0) { toast(L.stillDecoding, 'warning'); return; }
    if (pending.length) { toast(fill(L.pendingFiles, { list: pending.map((p) => p.fileName).join(', ') }), 'warning'); return; }

    const key = limitKey();
    const token = window.__toolLimit ? window.__toolLimit.begin(SLUG, key ? [key] : null) : { commit() {}, abort() {} };
    if (!token) return;

    stopPlayback();
    hideResult();
    exportAbort = new AbortController();
    const signal = exportAbort.signal;
    const fmt = $('pe-format').value;
    const kbps = parseInt($('pe-quality').value, 10) || 128;
    const chapters = chapterList(doc, untitled);
    paintNumbers();
    showProgress(true, L.measuring, 0);
    try { window.__stage && window.__stage('export_start', { fmt, kbps, tracks: doc.tracks.length, dur: Math.round(plan.duration) }); } catch (_) {}
    const t0 = performance.now();
    try {
        const out = await exportEpisode({
            doc, sources, format: fmt, bitrate: kbps, channels: channelsWanted(), signal, slug: SLUG,
            chapters,
            tags: {
                title: doc.meta.title, artist: doc.meta.author, album: doc.meta.show,
                track: doc.meta.episode, year: doc.meta.year, genre: 'Podcast',
            },
            cover: cover ? { data: cover.data, mime: cover.mime } : null,
            onProgress(stage, f) {
                if (stage === 'measure') showProgress(true, L.measuring, f * 0.3);
                else if (stage === 'render') showProgress(true, L.rendering, 0.3 + f * 0.62);
                else showProgress(true, L.encoding, 0.94);
            },
        });
        const ext = out.format;
        let name = baseName();
        if (window.__outputName && window.__outputName.join) name = window.__outputName.join(name, ext) || (name + '.' + ext);
        else name = name.replace(/[\/:*?"<>|]+/g, '_') + '.' + ext;
        result = { blob: out.blob, name, receipt: out.receipt, chapters };
        try { window.__exportKeepSave && window.__exportKeepSave(out.blob, name); } catch (_) {}
        token.commit();
        showProgress(false);
        showResult();
        if (draft) draft.exported();
        const r = out.receipt;
        ae('tool_complete', {
            format: ext, kbps, tracks: doc.tracks.length, clips: doc.clips.length, out_s: Math.round(r.duration),
            lufs: Math.round(r.lufs * 10) / 10, tp: Math.round(r.truePeakDb * 10) / 10, master: Math.round(r.masterDb * 10) / 10,
            lim: Math.round((r.limitedDb || 0) * 10) / 10, chapters: r.chapters, lane: r.lane, passes: r.passes, ch: r.channels,
            ms: Math.round(performance.now() - t0), mb: Math.round(out.blob.size / 1048576), disk: r.disk,
            level: doc.mix.level ? 1 : 0, duck: doc.mix.duck ? 1 : 0, bleed: doc.mix.bleed ? 1 : 0, cover: cover ? 1 : 0,
        });
        try { window.__aeToolComplete && window.__aeToolComplete({ format: ext, tracks: doc.tracks.length }); } catch (_) {}
        try { window.__stage && window.__stage('export_done', { fmt: ext, bytes: out.blob.size }); } catch (_) {}
    } catch (err) {
        token.abort();
        showProgress(false);
        const mine = window.__isUserCancel ? window.__isUserCancel(err, signal) : (err && err.name === 'AbortError');
        if (mine) { toast(L.cancelled, 'info'); }
        else {
            try { window.__handleError && window.__handleError(err, 'pe_export', { say: L.exportFail }); } catch (_) { toast(L.exportFail, 'error'); }
            lastErrors.push('export ' + String((err && (err.code || err.name)) || '') + ': ' + String((err && err.message) || err).slice(0, 160));
            ae('tool_error', { error_type: 'export_failed', code: (err && (err.code || err.name)) || '', msg: String((err && err.message) || err).slice(0, 120), fmt });
        }
    } finally {
        exportAbort = null;
        paintNumbers();
    }
}

function showResult() {
    const r = result.receipt;
    const el = $('pe-result');
    {
        el.hidden = false;
        setText($('pe-res-name'), result.name);
        const mb = result.blob.size / 1048576;
        const facts = [fmtClock(r.duration), (mb >= 10 ? Math.round(mb) : mb.toFixed(1)) + ' ' + (L.unitMb || 'MB')];
        facts.push(lufsText(r.lufs) + ' LUFS');
        if (isFinite(r.truePeakDb)) facts.push(fill(L.resPeak, { v: lufsText(r.truePeakDb) }));
        facts.push(r.channels === 1 ? L.mono : L.stereo);
        if (r.chapters) facts.push(fill(L.resChapters, { n: r.chapters }));
        setText($('pe-res-facts'), facts.join(' · '));
        const verdict = $('pe-res-verdict');
        if (r.targetLufs != null && isFinite(r.lufs)) {
            const off = Math.abs(r.lufs - r.targetLufs);
            verdict.hidden = false;
            verdict.className = 'pe-res-verdict ' + (off <= 1 ? 'pe-good' : 'pe-warn');
            verdict.textContent = fill(off <= 1 ? L.resOnTarget : L.resOffTarget, { target: lufsText(r.targetLufs), got: lufsText(r.lufs) });
        } else {
            verdict.hidden = true;
        }
        $('pe-res-copy').hidden = !result.chapters.length;
        setText($('pe-download-fmt'), String(result.name.split('.').pop() || '').toUpperCase());
        const au = $('pe-res-audio');
        try { if (au.dataset.url) URL.revokeObjectURL(au.dataset.url); } catch (_) {}
        au.dataset.url = URL.createObjectURL(result.blob);
        au.src = au.dataset.url;
    }
    say(L.resReady);
}

function hideResult() {
    const el = $('pe-result');
    if (el.hidden) return;
    el.hidden = true;
    const au = $('pe-res-audio');
    try { au.pause(); } catch (_) {}
    try { if (au.dataset.url) URL.revokeObjectURL(au.dataset.url); } catch (_) {}
    au.removeAttribute('src'); delete au.dataset.url;
    result = null;
}

async function copyText(text, okMsg) {
    try {
        await navigator.clipboard.writeText(text);
        toast(okMsg, 'success');
    } catch (_) {
        const ta = document.createElement('textarea');
        ta.value = text; ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(ta); ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch (__) {}
        ta.remove();
        toast(ok ? okMsg : L.copyFail, ok ? 'success' : 'warning');
    }
}

/* ── проект ──────────────────────────────────────────────────────────── */
function saveProject() {
    if (!doc.tracks.length) return;
    const data = toProject(doc, Array.from(sources.values()));
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const pd = window.__projectDoor;
    const pname = pd && pd.suggestName ? pd.suggestName(L.outputBase || 'podcast', 'json') : 'podcast-project.json';
    ae('feature_use', { f: 'project_saved', tracks: doc.tracks.length });
    const saver = pd && pd.saveFile
        ? pd.saveFile(blob, { key: SLUG, slug: SLUG, suggestedName: pname, types: [{ description: 'Timbrica project', accept: { 'application/json': ['.json'] } }] })
        : Promise.resolve(window.__downloadFile(blob, pname)).then((ok) => (ok === false ? null : { via: 'download', name: pname }));
    saver.then((res) => {
        if (!res || res.via === 'cancelled') return;
        const S = window.__projectDoorStrings || {};
        toast(res.via === 'handle' ? String(S.project_updated_msg || ':name').replace(':name', res.name) : L.projectSaved, 'success');
    }).catch((e) => { try { window.__handleError(e, 'pe_save_project', { say: L.projectSaveFail }); } catch (_) {} });
}

function loadProject(file) {
    const reader = new FileReader();
    reader.onload = () => {
        let back = null;
        try { back = fromProject(JSON.parse(reader.result)); } catch (_) { back = null; }
        if (!back) { toast(L.projectInvalid, 'error'); return; }
        openProject(back);
        ae('feature_use', { f: 'project_loaded', tracks: back.doc.tracks.length, live: sources.size });
    };
    reader.onerror = () => toast(L.projectInvalid, 'error');
    reader.readAsText(file);
}

/** Положить открытый проект на то, что уже загружено; остальное — ждать. */
function openProject(back) {
    stopPlayback();
    hideResult();
    restore(doc, snapshot(back.doc));
    history.reset();
    const live = Array.from(sources.values());
    const rows = back.sources.slice();
    // Соответствие «строка проекта — запись» решает общая дверь проектов: по
    // имени файла, с очередью на одноимённые.
    let pairs = [];
    let leftover = rows;
    const pd = window.__projectDoor;
    if (pd && pd.matchRows) {
        const m = pd.matchRows(rows, live, { nameOf: (s) => s.name });
        pairs = m.pairs || [];
        leftover = m.leftover || [];
    } else {
        leftover = [];
        const free = live.slice();
        for (const row of rows) {
            const i = free.findIndex((s) => sameName(s.name, row.fileName));
            if (i >= 0) pairs.push([free.splice(i, 1)[0], row]); else leftover.push(row);
        }
    }
    const map = {};
    const dur = {};
    for (const [src, row] of pairs) { map[row.id] = src.id; dur[src.id] = src.duration; }
    bindSources(doc, map, dur);
    pending = leftover.map((r) => ({ id: r.id, fileName: r.fileName }));
    cover = null;
    selTrack = doc.tracks.length ? doc.tracks[0].id : null;
    selClip = null;
    setRange(null);
    reveal();
    replan({ quiet: true });
    select(selTrack, null);
    fillSound();
    fillEpisode();
    notePending();
    timeline.zoomFit();
    const S = window.__projectDoorStrings || {};
    const total = back.sources.length;
    if (pairs.length && !leftover.length) toast(String(S.project_applied || L.projectLoaded).replace(':n', pairs.length), 'success');
    else if (pairs.length) toast(String(S.project_applied_partial || L.projectLoaded).replace(':n', pairs.length).replace(':m', total), 'warning');
    else toast(L.projectLoaded, 'info');
}

/* ── «Мои работы» ────────────────────────────────────────────────────── */
function draftPayload() {
    if (!doc.tracks.length || pending.length) return null;
    const list = Array.from(sources.values()).filter((s) => s.file && doc.clips.some((c) => c.src === s.id));
    if (!list.length) return null;
    const files = list.map((s) => s.file);
    const state = { v: 2, project: toProject(doc, list), fileOf: list.map((s) => s.id), cover: -1 };
    if (cover && cover.blob) { state.cover = files.length; state.coverSize = { w: cover.srcW, h: cover.srcH }; files.push(new File([cover.blob], 'cover.jpg', { type: 'image/jpeg' })); }
    let preview = '';
    try {
        const cv = document.createElement('canvas');
        cv.width = 160; cv.height = 90;
        const g = cv.getContext('2d');
        g.fillStyle = '#141824'; g.fillRect(0, 0, 160, 90);
        const src = $('pe-canvas');
        if (src.width > 0) g.drawImage(src, 0, RULER_H * (timeline.state.dpr || 1), src.width, src.height - RULER_H * (timeline.state.dpr || 1), 0, 6, 160, 78);
        preview = cv.toDataURL('image/jpeg', 0.7);
    } catch (_) {}
    return { state, files, label: baseName(), preview };
}

async function draftApply(d) {
    const st = d.state || {};
    const files = d.files || [];
    const back = st.project ? fromProject(st.project) : null;
    if (!back) return [];
    draftFresh();
    openProject({ doc: back.doc, sources: back.sources });
    const missing = [];
    const ids = st.fileOf || [];
    const ctx = player.decodeContext();
    busy += 1; paintNumbers();
    try {
        for (let i = 0; i < ids.length; i++) {
            const row = pending.find((p) => p.id === ids[i]);
            const f = files[i];
            if (!row) continue;
            if (!f) { missing.push(row.fileName); continue; }
            setBusy(fill(L.decoding, { name: row.fileName, i: i + 1, n: ids.length }), null);
            try {
                const src = await importFile(f, ctx, { slug: SLUG, role: ROLE_VOICE, limitKey: window.__toolLimit && window.__toolLimit.fileKey ? window.__toolLimit.fileKey(f) : null });
                src.name = row.fileName;
                sources.set(src.id, src);
                const map = {}; map[row.id] = src.id;
                const dur = {}; dur[src.id] = src.duration;
                bindSources(doc, map, dur);
                pending = pending.filter((p) => p !== row);
                replan({ quiet: true });
            } catch (_) { missing.push(row.fileName); }
        }
        if (st.cover >= 0 && files[st.cover]) { await pickCover(files[st.cover]); if (cover && st.coverSize) { cover.srcW = st.coverSize.w; cover.srcH = st.coverSize.h; paintCover(); } }
    } finally {
        busy -= 1; setBusy(null); paintNumbers();
    }
    notePending();
    select(doc.tracks.length ? doc.tracks[0].id : null, null);
    timeline.zoomFit();
    return missing;
}

function draftFresh() {
    stopPlayback();
    hideResult();
    restore(doc, snapshot(makeDoc()));
    history.reset();
    sources.clear();
    pending = [];
    cover = null;
    selTrack = null; selClip = null;
    setRange(null);
    closePauses();
    reveal();
    replan({ quiet: true });
    select(null, null);
    fillSound();
    fillEpisode();
    notePending();
}

/* ── запуск ──────────────────────────────────────────────────────────── */
function boot() {
    const canvas = $('pe-canvas');
    if (!canvas) return;

    player = createPlayer({
        getPlan: () => plan,
        onEnded() {
            cancelAnimationFrame(raf);
            timeline.setPlayhead(plan ? plan.duration : 0, false);
            setText($('pe-now'), fmtClock(plan ? plan.duration : 0, true));
            paintPlay();
        },
    });

    timeline = createTimeline({
        canvas,
        playhead: $('pe-playhead'),
        bar: $('pe-scroll'),
        thumb: $('pe-scroll-thumb'),
        getDoc: () => doc,
        getPlan: () => plan,
        getSource: (id) => sources.get(id),
        waitLabel: (c) => { const p = pending.find((x) => x.id === c.src); return p ? fill(L.waitsFile, { name: p.fileName }) : ''; },
        onSeek(t) { setText($('pe-now'), fmtClock(t, true)); player.seek(t); },
        onSelect(trackId, clipId) { select(trackId, clipId); },
        onRange(r, done) { if (done) setRange(r); else { range = r; } },
        onChapter(ch) { showTab('episode', true); const el = document.querySelector('.pe-ch[data-id="' + ch.id + '"] .pe-ch-name'); if (el) { try { el.focus({ preventScroll: true }); } catch (_) {} } },
        onEditStart() { gestureSnap = snapshot(doc); history.push(); },
        onMove(clip, at, whole) {
            if (whole) shiftTrack(doc, clip.track, at - clip.at); else moveClip(doc, clip.id, at);
            replan({ keepAudio: true, quiet: true });
            fillTrackTab();
        },
        onTrim(clip, side, t) {
            const src = sources.get(clip.src);
            trimClip(doc, clip.id, side, t, src ? src.duration : undefined);
            replan({ keepAudio: true, quiet: true });
            fillTrackTab();
        },
        onEditEnd(kind, clip, ok) {
            // Жест забрал браузер (началась прокрутка) — возвращаем всё как было.
            if (!ok && gestureSnap) restore(doc, gestureSnap);
            gestureSnap = null;
            history.dropIfUnchanged();
            replan();
            select(selTrack, selClip);
            if (ok) ae('feature_use', { f: kind === 'move' ? 'clip_moved' : 'clip_trimmed' });
        },
        onZoom(f, fit) { $('pe-zoom-fit').classList.toggle('on', !!fit); },
    });

    /* зона загрузки */
    const zone = $('pe-upload');
    const onFiles = (first, detail) => {
        const files = (detail && detail.files && detail.files.length) ? detail.files : (first ? [first] : []);
        addFiles(files, detail && detail.source);
    };
    if (window.__bindUploaderFile) window.__bindUploaderFile(zone, onFiles);
    else zone.addEventListener('tool-uploader:file', (e) => onFiles(e.detail && e.detail.file, e.detail));

    /* шапки дорожек */
    $('pe-heads').addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        const head = e.target.closest('.pe-head');
        if (!b || !head) return;
        const t = trackOf(doc, head.dataset.id);
        if (!t) return;
        if (b.dataset.act === 'pick') { select(t.id, null, { show: true }); return; }
        history.push();
        if (b.dataset.act === 'mute') t.mute = !t.mute; else t.solo = !t.solo;
        replan();
    });

    /* панель инструментов */
    $('pe-undo').addEventListener('click', undo);
    $('pe-redo').addEventListener('click', redo);
    $('pe-split').addEventListener('click', doSplit);
    $('pe-cut').addEventListener('click', doCut);
    $('pe-silence').addEventListener('click', doSilence);
    $('pe-crop').addEventListener('click', doCrop);
    $('pe-chapter').addEventListener('click', addChapterNow);
    $('pe-range-mode').addEventListener('click', () => {
        const on = !timeline.state.rangeMode;
        timeline.setRangeMode(on);
        $('pe-range-mode').classList.toggle('on', on);
        $('pe-range-mode').setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('pe-zoom-in').addEventListener('click', () => timeline.zoomIn());
    $('pe-zoom-out').addEventListener('click', () => timeline.zoomOut());
    $('pe-zoom-fit').addEventListener('click', () => { if (range) timeline.zoomTo(range.a, range.b); else timeline.zoomFit(); });
    $('pe-save').addEventListener('click', () => { saveProject(); closeMenu(); });
    $('pe-open').addEventListener('click', () => { $('pe-project-in').click(); closeMenu(); });
    $('pe-project-in').addEventListener('change', () => { const f = $('pe-project-in').files[0]; $('pe-project-in').value = ''; if (f) loadProject(f); });
    if (window.__projectDoor && window.__projectDoor.mount) {
        window.__projectDoor.mount('[data-project-door]', { slug: SLUG, onFile: loadProject, onOther: (f) => addFiles([f]) });
    }

    /* транспорт */
    $('pe-play').addEventListener('click', () => { if (player.isPlaying()) stopPlayback(); else startPlayback(); });
    $('pe-start').addEventListener('click', () => seek(0));
    $('pe-export').addEventListener('click', doExport);
    $('pe-cancel').addEventListener('click', () => { if (exportAbort) exportAbort.abort(); });
    $('pe-download').addEventListener('click', async () => {
        if (!result) return;
        const ok = window.__downloadFile ? await window.__downloadFile(result.blob, result.name) : false;
        ae('feature_use', { f: 'download', ok: ok === false ? 0 : 1 });
    });
    $('pe-res-copy').addEventListener('click', () => { if (result) copyText(chaptersAsText(result.chapters), L.chaptersCopied); });
    $('pe-res-close').addEventListener('click', hideResult);

    /* вкладки */
    document.querySelectorAll('#pe-tabs .tc-seg-b').forEach((b) => b.addEventListener('click', () => showTab(b.dataset.tab)));
    $('pe-tabs').addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        const tabs = Array.from(document.querySelectorAll('#pe-tabs .tc-seg-b'));
        const i = tabs.indexOf(document.activeElement);
        if (i < 0) return;
        const dir = (e.key === 'ArrowRight') === !timeline.isRtl() ? 1 : -1;
        const next = tabs[(i + dir + tabs.length) % tabs.length];
        next.focus(); showTab(next.dataset.tab);
        e.preventDefault();
    });

    /* дорожка */
    $('pe-t-name').addEventListener('input', () => editTrack((t) => { t.name = $('pe-t-name').value.slice(0, 80); }, 'name', true));
    $('pe-t-name').addEventListener('change', () => { liveKey = null; });
    document.querySelectorAll('#pe-t-role .tc-seg-b').forEach((b) => b.addEventListener('click', () => {
        editTrack((t) => { t.role = b.dataset.role; });
        ae('feature_use', { f: 'role_set', role: b.dataset.role });
    }));
    $('pe-t-gain').addEventListener('input', () => editTrack((t) => {
        t.gainDb = Math.max(LIMITS.gainDb.min, Math.min(LIMITS.gainDb.max, parseFloat($('pe-t-gain').value) || 0));
    }, 'gain', true));
    $('pe-t-gain').addEventListener('change', () => { liveKey = null; replan(); });
    $('pe-t-mute').addEventListener('click', () => editTrack((t) => { t.mute = !t.mute; }));
    $('pe-t-solo').addEventListener('click', () => editTrack((t) => { t.solo = !t.solo; }));
    $('pe-t-speed').addEventListener('change', () => {
        const v = parseFloat(String($('pe-t-speed').value).replace(',', '.'));
        if (!isFinite(v) || v <= 0) { fillTrackTab(); return; }
        history.push();
        setSpeed(doc, selTrack, v);
        replan(); fillTrackTab();
    });
    (window.__timeFieldQ = window.__timeFieldQ || []).push(() => {
        window.TimeField.bind($('pe-t-start'), {
            get: () => parseFloat($('pe-t-start').dataset.value) || 0,
            set: (v) => {
                if (!selTrack) return;
                const cur = parseFloat($('pe-t-start').dataset.value) || 0;
                commit(() => shiftTrack(doc, selTrack, v - cur));
            },
            min: 0, max: 36000, slug: SLUG, field: 'track_start',
            format: (s) => fmtClock(s, true),
            onInvalid: (raw) => toast(fill(L.timeUnreadable, { value: raw }), 'error'),
        });
    });
    const fadeField = (id, key) => $(id).addEventListener('change', () => {
        const c = selClip ? clipOf(doc, selClip) : null;
        if (!c) return;
        const max = clipLen(doc, c);
        const v = Math.max(0, Math.min(LIMITS.fade.max, max, parseFloat(String($(id).value).replace(',', '.')) || 0));
        commit(() => {
            c[key] = v;
            const other = key === 'fadeIn' ? 'fadeOut' : 'fadeIn';
            if (c[key] + c[other] > max) c[other] = Math.max(0, max - c[key]);
        });
    });
    fadeField('pe-c-fi', 'fadeIn');
    fadeField('pe-c-fo', 'fadeOut');
    $('pe-c-del').addEventListener('click', () => { if (selClip) { const id = selClip; commit(() => deleteClip(doc, id)); select(selTrack, null); } });
    $('pe-t-del').addEventListener('click', () => { if (selTrack) deleteTrack(selTrack); });

    /* звук */
    const mixToggle = (id, key) => $(id).addEventListener('change', () => {
        commit(() => { doc.mix[key] = $(id).checked; });
        fillSound();
        ae('feature_use', { f: 'mix_' + key, on: doc.mix[key] ? 1 : 0 });
    });
    mixToggle('pe-m-level', 'level');
    mixToggle('pe-m-duck', 'duck');
    mixToggle('pe-m-hpf', 'hpf');
    mixToggle('pe-m-bleed', 'bleed');
    let duckLive = false;
    $('pe-m-duckdb').addEventListener('input', () => {
        if (!duckLive) { history.push(); duckLive = true; }
        doc.mix.duckDb = Math.max(LIMITS.duckDb.min, Math.min(LIMITS.duckDb.max, parseInt($('pe-m-duckdb').value, 10) || 0));
        setText($('pe-m-duckdb-v'), '−' + doc.mix.duckDb + ' ' + (L.unitDb || 'dB'));
        replan({ minor: true });
    });
    $('pe-m-duckdb').addEventListener('change', () => { duckLive = false; replan(); });
    $('pe-m-target').addEventListener('change', () => {
        const v = $('pe-m-target').value;
        commit(() => { doc.mix.target = v === 'auto' || v === 'off' ? v : parseFloat(v); });
        ae('feature_use', { f: 'target_set', v });
    });
    $('pe-m-channels').addEventListener('change', () => { replan(); });
    if (window.__toolSettings) window.__toolSettings.bind(SLUG, $('pe-m-channels'), 'channels', 'auto');
    $('pe-sync').addEventListener('click', doSync);
    $('pe-pauses').addEventListener('click', () => { if ($('pe-pause-panel').hidden) openPauses(); else closePauses(); });
    $('pe-p-min').addEventListener('change', scanPauses);
    $('pe-p-keep').addEventListener('change', paintPauses);
    $('pe-p-apply').addEventListener('click', applyPauses);
    $('pe-p-close').addEventListener('click', closePauses);
    $('pe-p-list').addEventListener('change', (e) => {
        const i = e.target && e.target.dataset ? parseInt(e.target.dataset.i, 10) : NaN;
        if (!pauseList || !(i >= 0)) return;
        pauseList[i].off = !e.target.checked;
        paintPauses();
    });
    $('pe-p-list').addEventListener('click', (e) => {
        const b = e.target.closest('[data-go]');
        if (!b || !pauseList) return;
        e.preventDefault();
        const p = pauseList[parseInt(b.dataset.go, 10)];
        if (p) { seek(Math.max(0, p.from - 1.5)); startPlayback(Math.max(0, p.from - 1.5)); }
    });

    /* выпуск */
    [['pe-e-title', 'title'], ['pe-e-show', 'show'], ['pe-e-author', 'author'], ['pe-e-num', 'episode'], ['pe-e-year', 'year']]
        .forEach(([id, k]) => {
            let live = false;
            $(id).addEventListener('input', () => {
                if (!live) { history.push(); live = true; }
                doc.meta[k] = $(id).value.slice(0, 200);
                if (draft) draft.touch(false);
            });
            $(id).addEventListener('change', () => { live = false; if (draft) draft.touch(true); paintNumbers(); });
        });
    $('pe-e-cover-btn').addEventListener('click', () => $('pe-e-cover-in').click());
    $('pe-e-cover-in').addEventListener('change', () => { const f = $('pe-e-cover-in').files[0]; $('pe-e-cover-in').value = ''; pickCover(f); });
    $('pe-e-cover-del').addEventListener('click', () => {
        if (cover && cover.url) { try { URL.revokeObjectURL(cover.url); } catch (_) {} }
        cover = null; doc.meta.cover = null; paintCover(); if (draft) draft.touch(true);
    });
    $('pe-ch-add').addEventListener('click', addChapterNow);
    $('pe-ch-list').addEventListener('click', (e) => {
        const b = e.target.closest('[data-act]');
        const row = e.target.closest('.pe-ch');
        if (!b || !row) return;
        const ch = doc.chapters.find((c) => c.id === row.dataset.id);
        if (!ch) return;
        if (b.dataset.act === 'go') { seek(ch.t); return; }
        history.push(); removeChapter(doc, ch.id); replan({ minor: true }); paintChapters();
    });
    $('pe-ch-list').addEventListener('input', (e) => {
        const row = e.target.closest ? e.target.closest('.pe-ch') : null;
        if (!row || !e.target.classList.contains('pe-ch-name')) return;
        const ch = doc.chapters.find((c) => c.id === row.dataset.id);
        if (!ch) return;
        if (liveKey !== 'ch' + ch.id) { history.push(); liveKey = 'ch' + ch.id; }
        ch.title = e.target.value.slice(0, 120);
        if (draft) draft.touch(false);
    });
    $('pe-ch-list').addEventListener('change', () => { liveKey = null; normalizeChapters(doc); if (draft) draft.touch(true); });
    $('pe-ch-copy').addEventListener('click', () => copyText(chaptersAsText(chapterList(doc, untitled)), L.chaptersCopied));
    $('pe-ch-json').addEventListener('click', async () => {
        const blob = new Blob([chaptersAsJson(chapterList(doc, untitled))], { type: 'application/json' });
        let name = baseName() + '-chapters';
        name = window.__outputName && window.__outputName.join ? (window.__outputName.join(name, 'json') || name + '.json') : name + '.json';
        if (window.__downloadFile) await window.__downloadFile(blob, name);
        ae('feature_use', { f: 'chapters_json' });
    });

    fillFormats();
    fillSound();
    fillEpisode();
    showTab('track');
    replan({ quiet: true });
    select(null, null);

    /* клавиатура */
    document.addEventListener('keydown', (ev) => {
        if (!doc.tracks.length) return;
        // Чья клавиша — по её работе в элементе фокуса (общий примитив), а не по тегу:
        // ползунку громкости пробел, буквы и Delete ни к чему. Разбор — key-owned-by-focus.md.
        if (window.__keyOwnedByFocus) { if (window.__keyOwnedByFocus(ev)) return; }
        else {
            const tag = (ev.target && ev.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (ev.target && ev.target.isContentEditable)) return;
        }
        const mod = ev.ctrlKey || ev.metaKey;
        const step = ev.shiftKey ? 0.1 : 1;
        if (ev.code === 'Space') { ev.preventDefault(); if (!ev.repeat) $('pe-play').click(); }
        else if (mod && ev.code === 'KeyZ') { ev.preventDefault(); if (ev.shiftKey) redo(); else undo(); }
        else if (mod && ev.code === 'KeyY') { ev.preventDefault(); redo(); }
        else if (mod && ev.code === 'KeyS') { ev.preventDefault(); saveProject(); }
        else if (mod || ev.altKey) { return; }
        else if (ev.code === 'Home') { ev.preventDefault(); seek(0); }
        else if (ev.code === 'End') { ev.preventDefault(); seek(plan.duration); }
        else if (ev.code === 'ArrowLeft') { ev.preventDefault(); seek(nowTime() - step); }
        else if (ev.code === 'ArrowRight') { ev.preventDefault(); seek(nowTime() + step); }
        else if (ev.code === 'Equal' || ev.code === 'NumpadAdd') { ev.preventDefault(); timeline.zoomIn(); }
        else if (ev.code === 'Minus' || ev.code === 'NumpadSubtract') { ev.preventDefault(); timeline.zoomOut(); }
        else if (ev.code === 'Digit0') { ev.preventDefault(); timeline.zoomFit(); }
        else if (ev.code === 'KeyT') { ev.preventDefault(); doSplit(); }
        else if (ev.code === 'KeyB') { ev.preventDefault(); addChapterNow(); }
        else if (ev.code === 'KeyM' && selTrack) { ev.preventDefault(); history.push(); const t = trackOf(doc, selTrack); t.mute = !t.mute; replan(); }
        else if (ev.code === 'KeyS' && selTrack) { ev.preventDefault(); history.push(); const t = trackOf(doc, selTrack); t.solo = !t.solo; replan(); }
        else if (ev.code === 'KeyR') { ev.preventDefault(); $('pe-range-mode').click(); }
        else if (ev.code === 'Escape' && range) { ev.preventDefault(); setRange(null); }
        else if (ev.code === 'Delete' || ev.code === 'Backspace') { ev.preventDefault(); doDelete(); }
    });

    /* аппаратные кнопки и экран блокировки */
    try {
        if (navigator.mediaSession) {
            const ms = navigator.mediaSession;
            ms.setActionHandler('play', () => { if (!player.isPlaying()) startPlayback(); });
            ms.setActionHandler('pause', () => stopPlayback());
            ms.setActionHandler('seekbackward', () => seek(nowTime() - 10));
            ms.setActionHandler('seekforward', () => seek(nowTime() + 10));
        }
    } catch (_) {}

    window.addEventListener('pagehide', () => stopPlayback());
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => timeline.resize()).observe(canvas.parentElement);
    else window.addEventListener('resize', () => timeline.resize());
    window.addEventListener('wide-workspace:change', () => timeline.resize());

    /* «Мои работы»: выпуск переживает закрытую вкладку и перезагрузку */
    if (window.__toolDraft && window.__toolDraft.editor && $('peDrafts')) {
        draft = window.__toolDraft.editor({
            slug: SLUG, root: 'peDrafts', icon: '🎙️', openKey: 'pe-draft-open',
            payload: draftPayload, apply: draftApply, fresh: draftFresh,
            started: () => doc.tracks.length > 0,
        });
        try { Promise.resolve(draft.boot()).catch(() => {}); } catch (_) {}
    }

    window.__peTimeline = timeline;
}

function closeMenu() {
    const m = $('pe-menu');
    try { if (m && m.hidePopover && m.matches(':popover-open')) m.hidePopover(); } catch (_) {}
}

/* Зонд для стендов: состояние, которого не видно на экране. Не UI. */
window.__peProbe = () => ({
    tracks: doc.tracks.map((t) => ({ id: t.id, name: t.name, role: t.role, gainDb: t.gainDb, speed: t.speed, mute: t.mute, solo: t.solo })),
    clips: doc.clips.map((c) => ({ id: c.id, track: c.track, at: +c.at.toFixed(4), in: +c.in.toFixed(4), out: +c.out.toFixed(4) })),
    chapters: doc.chapters.map((c) => ({ t: +c.t.toFixed(3), title: c.title })),
    duration: plan ? +plan.duration.toFixed(3) : 0,
    channels: plan ? plan.channels : 0,
    estLufs: plan ? plan.estLufs : null,
    masterDb: plan ? plan.masterDb : 0,
    auto: plan ? plan.tracks.map((t) => +t.auto.toFixed(2)) : [],
    pending: pending.length,
    busy,
    range,
    playing: player ? player.isPlaying() : false,
    time: player ? player.time() : 0,
    result: result ? { name: result.name, size: result.blob.size, receipt: result.receipt } : null,
    canUndo: history.canUndo(),
    errors: lastErrors.slice(-6),
    sources: Array.from(sources.values()).map((x) => ({ id: x.id, name: x.name, duration: +x.duration.toFixed(3), ch: x.ch })),
    mix: Object.assign({}, doc.mix),
    pauses: pauseList ? pauseList.length : null,
    note: ($('pe-sound-note') && !$('pe-sound-note').hidden) ? $('pe-sound-note').textContent : '',
});
window.__peDoc = () => doc;
window.__peResultBlob = () => (result ? result.blob : null);
window.__peSetRange = (a, b) => setRange({ a, b });
window.__peSelect = (i) => { const t = doc.tracks[i]; if (t) select(t.id, null); };

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
